import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import NewProduct from "./pages/NewProduct";
import Categories from "./pages/Categories";
import Movements from "./pages/Movements";
import Settings from "./pages/Settings";
import POS from "./pages/POS";
import Sales from "./pages/Sales";
import SaleDetail from "./pages/SaleDetail";
import Users from "./pages/Users";
import ChangePassword from "./pages/ChangePassword";
import AuditLog from "./pages/AuditLog";
import Shifts from "./pages/Shifts";
import ShiftDetail from "./pages/ShiftDetail";
import Guide from "./pages/Guide";
import SystemCenter from "./pages/SystemCenter";
import Bootstrap from "./pages/Bootstrap";
import Setup2FA from "./pages/Setup2FA";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Security from "./pages/Security";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-stone-500">
        Yükleniyor...
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/sifre-degistir" replace />;
  if (user.must_setup_2fa && user.role === "owner") return <Navigate to="/2fa-kur" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "cashier") return <Navigate to="/satis" replace />;
  return children;
}

function OwnerOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner") return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.must_change_password) return <Navigate to="/sifre-degistir" replace />;
    return <Navigate to={user.role === "cashier" ? "/satis" : "/"} replace />;
  }
  return children;
}

function ForcedPasswordChange({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/sifre-degistir" replace />;
  return <Navigate to={user.role === "cashier" ? "/satis" : "/"} replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "#fff",
                border: "1px solid #E8DEC8",
                color: "#1c1917",
              },
            }}
          />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/ilk-kurulum" element={<Bootstrap />} />
            <Route path="/2fa-kur" element={<Setup2FA />} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-sifirla" element={<ResetPassword />} />
            <Route path="/sifre-degistir" element={<ForcedPasswordChange><ChangePassword /></ForcedPasswordChange>} />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/" element={<AdminOnly><Dashboard /></AdminOnly>} />
              <Route path="/satis" element={<POS />} />
              <Route path="/satislar" element={<Sales />} />
              <Route path="/satislar/:id" element={<SaleDetail />} />
              <Route path="/vardiyalar" element={<Shifts />} />
              <Route path="/vardiyalar/:id" element={<ShiftDetail />} />
              <Route path="/urunler" element={<AdminOnly><Products /></AdminOnly>} />
              <Route path="/yeni-urun" element={<AdminOnly><NewProduct /></AdminOnly>} />
              <Route path="/kategoriler" element={<AdminOnly><Categories /></AdminOnly>} />
              <Route path="/stok-hareketleri" element={<AdminOnly><Movements /></AdminOnly>} />
              <Route path="/kullanicilar" element={<AdminOnly><Users /></AdminOnly>} />
              <Route path="/kasiyerler" element={<Navigate to="/kullanicilar" replace />} />
              <Route path="/audit" element={<OwnerOnly><AuditLog /></OwnerOnly>} />
              <Route path="/kilavuz" element={<OwnerOnly><Guide /></OwnerOnly>} />
              <Route path="/sistem-merkezi" element={<OwnerOnly><SystemCenter /></OwnerOnly>} />
              <Route path="/guvenlik" element={<Security />} />
              <Route path="/ayarlar" element={<AdminOnly><Settings /></AdminOnly>} />
            </Route>
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
